/**
 * 신규 접수 일시정지 고객 안내 — 15개 지원 로케일.
 *
 * 서버는 안전한 enum 사유만 공개한다. 운영자 자유 입력 메모는 이 파일의 입력이나
 * 출력 계약에 포함하지 않아 고객 화면으로 유출될 수 없다(#809/admin#2181).
 */
import type { Locale } from '@/config/locales';
import {
  isWaitlistPauseReason,
  type WaitlistPauseReason,
} from '@/lib/api/waitlist';

export interface WaitlistPauseCopy {
  badge: string;
  title: string;
  description: string;
  reason: Record<WaitlistPauseReason, string>;
}

const PAUSE_COPY: Record<Locale, WaitlistPauseCopy> = {
  es: {
    badge: 'Nuevos registros en pausa',
    title: 'La fila está temporalmente en pausa',
    description: 'Quienes ya tienen turno conservan su lugar. Consulta otra fila disponible o vuelve a intentarlo en unos minutos.',
    reason: {
      capacity: 'La fila alcanzó su capacidad actual.',
      out_of_stock: 'Algunos productos no están disponibles por el momento.',
      closing: 'El establecimiento está próximo a cerrar.',
      manual: 'El establecimiento pausó temporalmente los nuevos registros.',
      other: 'Consulta al personal para obtener más información.',
    },
  },
  en: {
    badge: 'New entries paused',
    title: 'This queue is temporarily paused',
    description: 'Guests who already have a number keep their place. Choose another available queue or try again in a few minutes.',
    reason: {
      capacity: 'The queue has reached its current capacity.',
      out_of_stock: 'Some items are temporarily unavailable.',
      closing: 'The store is getting ready to close.',
      manual: 'The store temporarily paused new entries.',
      other: 'Please ask the staff for more information.',
    },
  },
  ko: {
    badge: '신규 접수 일시정지',
    title: '현재 신규 대기 접수가 일시정지되었습니다',
    description: '이미 대기번호를 받은 고객의 순서는 유지됩니다. 다른 대기열을 선택하거나 잠시 후 다시 확인해 주세요.',
    reason: {
      capacity: '현재 대기열 수용 한도에 도달했습니다.',
      out_of_stock: '일부 상품의 재고가 일시적으로 없습니다.',
      closing: '매장 마감을 준비하고 있습니다.',
      manual: '매장에서 신규 접수를 잠시 중단했습니다.',
      other: '자세한 내용은 매장 직원에게 문의해 주세요.',
    },
  },
  pt: {
    badge: 'Novas entradas pausadas',
    title: 'Esta fila está temporariamente pausada',
    description: 'Quem já tem senha mantém seu lugar. Escolha outra fila disponível ou tente novamente em alguns minutos.',
    reason: {
      capacity: 'A fila atingiu a capacidade atual.',
      out_of_stock: 'Alguns itens estão temporariamente indisponíveis.',
      closing: 'O estabelecimento está se preparando para fechar.',
      manual: 'O estabelecimento pausou temporariamente novas entradas.',
      other: 'Consulte a equipe para mais informações.',
    },
  },
  fr: {
    badge: 'Nouvelles inscriptions en pause',
    title: 'Cette file est temporairement en pause',
    description: 'Les clients ayant déjà un numéro gardent leur place. Choisissez une autre file disponible ou réessayez dans quelques minutes.',
    reason: {
      capacity: 'La file a atteint sa capacité actuelle.',
      out_of_stock: 'Certains articles sont temporairement indisponibles.',
      closing: 'L’établissement se prépare à fermer.',
      manual: 'L’établissement a temporairement suspendu les nouvelles inscriptions.',
      other: 'Demandez plus d’informations au personnel.',
    },
  },
  zh: {
    badge: '暂停新取号',
    title: '该队列暂时停止新取号',
    description: '已取号的顾客仍保留原有顺序。请选择其他可用队列，或稍后再试。',
    reason: {
      capacity: '当前队列已达到容量上限。',
      out_of_stock: '部分商品暂时缺货。',
      closing: '门店正在准备打烊。',
      manual: '门店暂时停止了新取号。',
      other: '详情请咨询店员。',
    },
  },
  ja: {
    badge: '新規受付を一時停止中',
    title: 'この待ち列は一時停止中です',
    description: 'すでに番号をお持ちの方の順番は維持されます。別の待ち列を選ぶか、しばらくしてから再度お試しください。',
    reason: {
      capacity: '現在の待ち列が上限に達しました。',
      out_of_stock: '一部の商品が一時的に品切れです。',
      closing: '閉店準備中です。',
      manual: '店舗が新規受付を一時停止しました。',
      other: '詳しくはスタッフにお尋ねください。',
    },
  },
  hi: {
    badge: 'नई प्रविष्टियाँ रुकी हुई हैं',
    title: 'यह कतार अस्थायी रूप से रुकी हुई है',
    description: 'जिनके पास पहले से नंबर है उनका स्थान बना रहेगा। कोई दूसरी उपलब्ध कतार चुनें या कुछ मिनट बाद फिर प्रयास करें।',
    reason: {
      capacity: 'कतार अपनी वर्तमान क्षमता तक पहुँच गई है।',
      out_of_stock: 'कुछ वस्तुएँ फिलहाल उपलब्ध नहीं हैं।',
      closing: 'स्टोर बंद होने की तैयारी कर रहा है।',
      manual: 'स्टोर ने नई प्रविष्टियाँ अस्थायी रूप से रोक दी हैं।',
      other: 'अधिक जानकारी के लिए कर्मचारियों से पूछें।',
    },
  },
  de: {
    badge: 'Neue Einträge pausiert',
    title: 'Diese Warteschlange ist vorübergehend pausiert',
    description: 'Gäste mit einer Nummer behalten ihren Platz. Wähle eine andere verfügbare Warteschlange oder versuche es später erneut.',
    reason: {
      capacity: 'Die Warteschlange hat ihre aktuelle Kapazität erreicht.',
      out_of_stock: 'Einige Artikel sind vorübergehend nicht verfügbar.',
      closing: 'Das Geschäft bereitet die Schließung vor.',
      manual: 'Das Geschäft hat neue Einträge vorübergehend pausiert.',
      other: 'Bitte frage das Personal nach weiteren Informationen.',
    },
  },
  it: {
    badge: 'Nuovi ingressi in pausa',
    title: 'Questa coda è temporaneamente in pausa',
    description: 'Chi ha già un numero mantiene il proprio posto. Scegli un’altra coda disponibile o riprova tra qualche minuto.',
    reason: {
      capacity: 'La coda ha raggiunto la capacità attuale.',
      out_of_stock: 'Alcuni articoli non sono temporaneamente disponibili.',
      closing: 'Il locale si sta preparando alla chiusura.',
      manual: 'Il locale ha sospeso temporaneamente i nuovi ingressi.',
      other: 'Chiedi maggiori informazioni al personale.',
    },
  },
  ru: {
    badge: 'Новые записи приостановлены',
    title: 'Эта очередь временно приостановлена',
    description: 'Гости, уже получившие номер, сохраняют своё место. Выберите другую доступную очередь или повторите попытку через несколько минут.',
    reason: {
      capacity: 'Очередь достигла текущего лимита.',
      out_of_stock: 'Некоторые позиции временно недоступны.',
      closing: 'Заведение готовится к закрытию.',
      manual: 'Заведение временно приостановило новые записи.',
      other: 'Уточните подробности у персонала.',
    },
  },
  ar: {
    badge: 'إيقاف التسجيلات الجديدة مؤقتًا',
    title: 'هذه قائمة الانتظار متوقفة مؤقتًا',
    description: 'يحتفظ من لديه رقم بالفعل بمكانه. اختر قائمة انتظار أخرى متاحة أو حاول مجددًا بعد بضع دقائق.',
    reason: {
      capacity: 'بلغت قائمة الانتظار سعتها الحالية.',
      out_of_stock: 'بعض الأصناف غير متوفرة مؤقتًا.',
      closing: 'يستعد المتجر للإغلاق.',
      manual: 'أوقف المتجر التسجيلات الجديدة مؤقتًا.',
      other: 'يرجى سؤال الموظفين لمزيد من المعلومات.',
    },
  },
  vi: {
    badge: 'Tạm dừng lượt đăng ký mới',
    title: 'Hàng chờ này đang tạm dừng',
    description: 'Khách đã có số vẫn giữ nguyên vị trí. Hãy chọn hàng chờ khác hoặc thử lại sau vài phút.',
    reason: {
      capacity: 'Hàng chờ đã đạt sức chứa hiện tại.',
      out_of_stock: 'Một số mặt hàng đang tạm hết.',
      closing: 'Cửa hàng đang chuẩn bị đóng cửa.',
      manual: 'Cửa hàng đã tạm dừng lượt đăng ký mới.',
      other: 'Vui lòng hỏi nhân viên để biết thêm thông tin.',
    },
  },
  th: {
    badge: 'พักการรับคิวใหม่',
    title: 'คิวนี้หยุดรับชั่วคราว',
    description: 'ผู้ที่มีหมายเลขคิวแล้วจะยังคงลำดับเดิม กรุณาเลือกคิวอื่นที่เปิดอยู่หรือลองอีกครั้งในอีกสักครู่',
    reason: {
      capacity: 'คิวถึงความจุสูงสุดในขณะนี้แล้ว',
      out_of_stock: 'สินค้าบางรายการหมดชั่วคราว',
      closing: 'ร้านกำลังเตรียมปิด',
      manual: 'ร้านพักการรับคิวใหม่ชั่วคราว',
      other: 'โปรดสอบถามพนักงานสำหรับข้อมูลเพิ่มเติม',
    },
  },
  nl: {
    badge: 'Nieuwe aanmeldingen gepauzeerd',
    title: 'Deze wachtrij is tijdelijk gepauzeerd',
    description: 'Gasten die al een nummer hebben behouden hun plaats. Kies een andere beschikbare rij of probeer het over een paar minuten opnieuw.',
    reason: {
      capacity: 'De wachtrij heeft de huidige capaciteit bereikt.',
      out_of_stock: 'Sommige artikelen zijn tijdelijk niet beschikbaar.',
      closing: 'De zaak maakt zich klaar om te sluiten.',
      manual: 'De zaak heeft nieuwe aanmeldingen tijdelijk gepauzeerd.',
      other: 'Vraag het personeel om meer informatie.',
    },
  },
};

export function getWaitlistPauseCopy(locale: Locale): WaitlistPauseCopy {
  return PAUSE_COPY[locale] ?? PAUSE_COPY.es;
}

export function getWaitlistPauseReasonText(
  locale: Locale,
  reason: unknown,
): string {
  // 상태 조회와 제출 에러 어느 경로에서도 알 수 없는 값이나 자유 입력 메모를 그대로
  // 렌더하지 않는다. 고객 공개 enum 외 값은 일반적인 수동 일시정지 안내로 축약한다.
  const safeReason: WaitlistPauseReason = isWaitlistPauseReason(reason) ? reason : 'manual';
  return getWaitlistPauseCopy(locale).reason[safeReason];
}
